import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  HiOutlineChatBubbleLeftRight,
  HiOutlineUsers,
  HiOutlineDevicePhoneMobile,
  HiOutlineUserGroup,
  HiOutlineDocumentText,
  HiOutlineCreditCard,
  HiOutlineClipboardDocumentList,
  HiOutlineCog6Tooth,
  HiOutlineArrowRightOnRectangle,
  HiOutlineBars3,
  HiOutlineXMark,
  HiOutlineKey,
  HiOutlineCodeBracket,
  HiOutlineGlobeAlt
} from 'react-icons/hi2';

const navItems = [
  { to: '/conversations', label: 'Conversations', icon: HiOutlineChatBubbleLeftRight },
  { to: '/contacts', label: 'Contacts', icon: HiOutlineUsers },
  { to: '/sessions', label: 'WhatsApp', icon: HiOutlineDevicePhoneMobile, roles: ['owner', 'admin'] },
  { to: '/users', label: 'Team', icon: HiOutlineUserGroup, roles: ['owner', 'admin'] },
  { to: '/invoices', label: 'Invoices', icon: HiOutlineDocumentText, roles: ['owner', 'admin'] },
  { to: '/subscription', label: 'Billing', icon: HiOutlineCreditCard, roles: ['owner'] },
  { to: '/audit-logs', label: 'Audit Logs', icon: HiOutlineClipboardDocumentList, roles: ['owner', 'admin'] },
  { to: '/api-keys', label: 'API Keys', icon: HiOutlineKey, roles: ['owner', 'admin'] },
  { to: '/webhooks', label: 'Webhooks', icon: HiOutlineGlobeAlt, roles: ['owner', 'admin'] },
  { to: '/developer', label: 'API Docs', icon: HiOutlineCodeBracket, roles: ['owner', 'admin'] },
  { to: '/settings', label: 'Settings', icon: HiOutlineCog6Tooth }
];

export default function Sidebar() {
  const { user, tenant, logoutUser } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const filteredItems = navItems.filter(
    (item) => !item.roles || item.roles.includes(user?.role)
  );

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary-500 text-white'
        : 'text-dark-500 hover:bg-dark-100 hover:text-dark-800'
    }`;

  const sidebarContent = (
    <>
      <div className="p-4 border-b border-dark-200">
        <h1 className="text-lg font-bold text-primary-500 truncate">{tenant?.company_name || 'WhatsApp SaaS'}</h1>
        <p className="text-xs text-dark-400 truncate mt-0.5">{user?.email}</p>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={linkClass}
            onClick={() => setMobileOpen(false)}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-dark-200">
        <button
          onClick={logoutUser}
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full transition-colors"
        >
          <HiOutlineArrowRightOnRectangle className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        className="lg:hidden fixed top-3 left-3 z-50 p-2 bg-white rounded-lg shadow-md"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <HiOutlineXMark className="w-6 h-6" /> : <HiOutlineBars3 className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-dark-200 flex flex-col transform transition-transform lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
